import { NextBestActionEnum } from '../../../../graphql.generated';

export type ConfirmationStepProps = {
    title: string;
    url: string;
    copy?: string | string[];
    cta: string;
};

export type ValidNextBestActions = Exclude<
    Exclude<NextBestActionEnum, NextBestActionEnum.UpdateYourSituation>,
    NextBestActionEnum.EmailMarketingConsent
>;

export type NextBestActionCopy = {
    [key in ValidNextBestActions]: Record<string, ConfirmationStepProps>;
};

export const nextBestActionCopy: NextBestActionCopy = {
    REMORTGAGE: {
        DEFAULT: {
            title: 'Your current mortgage deal is due to end soon',
            copy: 'Speak to the experts at Mojo and secure a new deal now',
            cta: 'Get free mortgage advice',
            url: 'https://zoopla.mojomortgages.com/get-started?utm_source=partner&utm_medium=zoopla&utm_campaign=next_best_action'
        }
    },
    ACQUIRE_NEW_HOMEOWNERS: {
        HOMEOWNER: {
            title: 'Find out what your home is worth',
            copy: 'Our instant valuation is the quickest and easiest way to know your home’s value.',
            cta: 'Get valuation',
            url: '/home-values/'
        },
        DERIVED_SELLER: {
            title: 'Find out what your home is worth',
            copy: 'Our instant valuation is the quickest and easiest way to know your home’s value.',
            cta: 'Get valuation',
            url: '/home-values/'
        },
        DECLARED_SELLER: {
            title: 'Find out what your home is worth',
            copy: 'Our instant valuation is the quickest and easiest way to know your home’s value.',
            cta: 'Get valuation',
            url: '/home-values/'
        }
    },
    CLAIM_HOME: {
        SINGLE: {
            title: 'You’re tracking {address} but if you own it let us know, and take advantage of our trusted data.',
            copy: 'Get insights on the local market, work out your equity and get monthly valuation emails.',
            cta: 'Set property relationship',
            url: '/favourites/tracked-properties/'
        },
        MULTIPLE: {
            title: 'Let us know which of your {number} tracked properties you call home and take advantage of our trusted data.',
            copy: 'Get insights on the local market, work out your equity and get monthly valuation emails.',
            cta: 'Set property relationship',
            url: '/favourites/tracked-properties/'
        }
    },
    CONTACTABLE_HOMEOWNERS: {
        DEFAULT: {
            title: 'Get a monthly valuation email for {address}',
            copy: 'Plus updates about your home and the local area with our insights email.',
            cta: 'Sign up',
            url: '/my-home/'
        }
    },
    APP_DOWNLOADS: {
        DEFAULT: {
            title: 'Keep up to date on-the-go with the Zoopla app.',
            copy: 'Plus get access to property searches, tracked properties and your home’s value whenever you want.',
            cta: 'Download the app',
            url: 'https://zoopla.onelink.me/3766088558/hkkhxtkg'
        }
    },
    NEW_HOME_LEADS: {
        HIGH_NEW_HOME_INTENT: {
            title: 'Check out the complete guide to buying a new-build home',
            copy: "From the buying process to 10-year warranties, it's all covered in our guide to new builds.",
            cta: 'New build guide',
            url: '/discover/buying/new-build-homes-guide/'
        },
        DECLARED_BUYER: {
            title: 'We can help first-time buyers like you with info on the best financial schemes available.',
            copy: "A buying scheme could help you buy your first place sooner. Find out if you're eligible and weigh up the pros and cons of each scheme.",
            cta: 'First-time buyer schemes',
            url: '/discover/buying/which-first-time-buyer-scheme-is-right-for-you/'
        },
        DERIVED_BUYER: {
            title: 'A buying scheme could help you buy your first place sooner.',
            copy: "Find out if you're eligible and weigh up the pros and cons of each scheme.",
            cta: 'First-time buyer schemes',
            url: '/discover/buying/which-first-time-buyer-scheme-is-right-for-you/'
        }
    },
    CONVERT_SAVED_SEARCH_TO_ALERTS: {
        DEFAULT: {
            title: 'Set up an email alert for your saved property searches and we’ll keep you updated.',
            copy: 'You’ll be the first to know when a new property is added to the market, helping you stay ahead of the competition.',
            cta: 'Set-up alert',
            url: '/myaccount/alerts-searches/'
        }
    },
    FOR_SALE_ENGAGEMENT: {
        FIRST: {
            title: "If you're looking to buy, use our stamp duty calculator to find out what you'll pay when you buy your next home.",
            cta: 'Stamp duty calculator',
            url: '/discover/buying/stamp-duty-calculator/'
        },
        SECOND: {
            title: "If you're looking to buy a property, check out our 7 handy tools to help you find your next home.",
            copy: "We've got all you need from mapping out your favourite streets to checking travel times to the local pub.",
            cta: 'Read the guide',
            url: '/discover/buying/9-tools-on-zoopla-that-will-make-you-a-property-expert/'
        },
        THIRD: {
            title: 'Looking to buy but still undecided where?',
            copy: "Here's our guide to finding the perfect location for you and your family.",
            cta: 'Read the guide',
            url: '/discover/buying/how-to-check-out-the-area-you-want-to-move-to/'
        }
    },
    VENDOR_LEAD_NUDGE: {
        DERIVED_WITH_ADDRESS: {
            title: "If you're looking to sell {address} why not get a local agent valuation.",
            copy: [
                'Browse the top performing agents in your area',
                'Book an in-person valuation and take advantage of their local knowledge',
                'Sell your home quickly and easily'
            ],
            cta: 'Start your booking now',
            url: '/agent-matcher/?uprn={uprn}'
        },
        DECLARED_WITH_ADDRESS: {
            title: "You told us you're planning to sell {address}, so why not get a local agent valuation.",
            copy: [
                'Browse the top performing agents in your area',
                'Book an in-person valuation and take advantage of their local knowledge',
                'Sell your home quickly and easily'
            ],
            cta: 'Start your booking now',
            url: '/agent-matcher/?uprn={uprn}'
        },
        DERIVED: {
            title: "If you're planning to sell, why not get an in-person valuation in the diary and see how much your home is worth.",
            copy: "You can take advantage of your local agents' knowledge, show off your property's unique features, and get the most accurate valuation.",
            cta: 'Search local agents',
            url: '/valuation/'
        },
        DECLARED: {
            title: "You told us you're planning to sell, so now's the time to book an in-person valuation.",
            copy: "You can take advantage of your local agents' knowledge, show off your property's unique features, and get the most accurate valuation.",
            cta: 'Search local agents',
            url: '/valuation/'
        }
    }
};
